import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { authApi } from '../lib/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location?.pathname || '';
  const isFR = pathname.startsWith('/fr');
  const signInHref = isFR ? '/fr/signin' : '/signin';

  const token = searchParams.get('token') || '';
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) setError(isFR ? 'Lien invalide ou expiré.' : 'Invalid or expired link.');
  }, [token, isFR]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 6) {
      setError(isFR ? 'Le mot de passe doit faire au moins 6 caractères.' : 'Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(isFR ? 'Les deux mots de passe ne correspondent pas.' : 'Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await authApi.resetPassword(token, newPassword);
      setDone(true);
    } catch (err) {
      setError((err instanceof Error ? err.message : '') || (isFR ? 'Lien invalide ou expiré.' : 'Invalid or expired link.'));
    } finally {
      setLoading(false);
    }
  };

  if (!token && !error) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#3C59B2]/10 flex items-center justify-center p-4">
      <div className="font-manrope w-full max-w-lg bg-white border border-[#E2E8F0] rounded-3xl overflow-hidden card-hover shadow-xl">
        <div className="bg-[#3C59B2] p-8 text-white">
          <a href={signInHref} className="flex items-center space-x-2 mb-6 cursor-pointer hover:opacity-80 transition-opacity inline-block">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">{isFR ? 'Retour' : 'Back'}</span>
          </a>
          <h1 className="text-2xl font-bold">
            {isFR ? 'Nouveau mot de passe' : 'New password'}
          </h1>
          <p className="text-blue-100 mt-2">
            {isFR ? 'Choisissez un nouveau mot de passe (min. 6 caractères).' : 'Choose a new password (min. 6 characters).'}
          </p>
        </div>
        <div className="p-8">
          {done ? (
            <div className="text-center">
              <p className="text-gray-700 mb-4 text-green-700 font-medium">
                {isFR ? 'Mot de passe mis à jour. Vous pouvez vous connecter.' : 'Password updated. You can now sign in.'}
              </p>
              <button
                type="button"
                onClick={() => navigate(signInHref)}
                className="btn-premium overflow-hidden px-6 py-3 rounded-full font-semibold bg-[#3C59B2] text-white hover:bg-[#2d4a8f]"
              >
                {isFR ? 'Se connecter' : 'Sign in'}
              </button>
            </div>
          ) : error && !token ? (
            <div className="text-center">
              <p className="text-red-600 mb-4">{error}</p>
              <a href={signInHref} className="text-[#3C59B2] hover:underline font-medium">
                {isFR ? 'Retour à la connexion' : 'Back to sign in'}
              </a>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {isFR ? 'Nouveau mot de passe' : 'New password'}
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full border border-gray-300 rounded-xl px-4 py-3 mb-4 focus:ring-2 focus:ring-[#3C59B2] focus:border-[#3C59B2]"
                minLength={6}
                disabled={loading}
                autoFocus
              />
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {isFR ? 'Confirmer le mot de passe' : 'Confirm password'}
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full border border-gray-300 rounded-xl px-4 py-3 mb-4 focus:ring-2 focus:ring-[#3C59B2] focus:border-[#3C59B2]"
                minLength={6}
                disabled={loading}
              />
              {error && <p className="text-red-600 text-sm mb-4">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="btn-premium overflow-hidden w-full py-3 rounded-full font-semibold bg-[#3C59B2] text-white hover:bg-[#2d4a8f] disabled:opacity-50"
              >
                {loading ? (isFR ? 'Enregistrement...' : 'Updating...') : (isFR ? 'Enregistrer' : 'Update password')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
